import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, FileUp, PanelLeftClose } from 'lucide-react'

import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui'
import { Button } from '@/components/ui/button'
import {
  isAllowUpload,
  useDeliverableSetById,
} from '@/features/deliverable-set'
import { buildDeliverableSetRoute } from '@/utils/routes'

import { useAuthStore } from '../../../auth'
import { ERoleType } from '../../../user-management'

interface TFLSidebarHeaderProps {
  deliverableSetId: string
  isSidebarCollapsed: boolean
  onBackClick: () => void
  onUpload: () => void
  onToggleCollapse: () => void
}

export const TFLSidebarHeader = ({
  deliverableSetId,
  isSidebarCollapsed,
  onBackClick,
  onUpload,
  onToggleCollapse,
}: TFLSidebarHeaderProps) => {
  const { data: deliverableSet } = useDeliverableSetById(deliverableSetId)
  const roles = useAuthStore((state) => state.roles)

  // Check if user has ONLY reviewer/viewer roles
  const isReviewerOrViewer = useMemo(() => {
    return (
      roles &&
      roles.length > 0 &&
      roles.every(
        (role) =>
          role.role_name === ERoleType.REVIEWER ||
          role.role_name === ERoleType.VIEWER
      )
    )
  }, [roles])

  return (
    <div className="flex flex-col gap-2">
      <div
        className={`flex items-center ${
          isSidebarCollapsed ? 'w-12 justify-center' : 'justify-between'
        }`}
      >
        {!isSidebarCollapsed && (
          <div className="flex-1 flex items-center gap-2">
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <Link
                  to={buildDeliverableSetRoute(deliverableSetId)}
                  className="no-underline flex items-center justify-center group"
                  onClick={onBackClick}
                >
                  <ArrowLeft
                    size={20}
                    className="size-5 text-foreground-fg-quaternary-400 group-hover:text-quaternary-500"
                  />
                </Link>
              </TooltipTrigger>
              <TooltipContent>
                <span>Back</span>
              </TooltipContent>
            </Tooltip>

            {deliverableSet &&
              isAllowUpload(deliverableSet) &&
              !isReviewerOrViewer && (
                <>
                  <Tooltip delayDuration={0}>
                    <TooltipTrigger asChild>
                      <Button
                        variant="text"
                        className="size-8 p-0 hover:bg-gray-50 flex-shrink-0 ml-auto group"
                        onClick={onUpload}
                        aria-label="Upload"
                      >
                        <FileUp className="size-5 text-foreground-fg-quaternary-400 group-hover:text-quaternary-500" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <span>Upload</span>
                    </TooltipContent>
                  </Tooltip>

                  <div className="h-4 border-r border-y-0 border-l-0 border-secondary border-solid mr-2" />
                </>
              )}
          </div>
        )}

        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <Button
              variant="text"
              className="size-8 p-0 hover:bg-gray-50 flex-shrink-0 group"
              onClick={onToggleCollapse}
              title={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-label={
                isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'
              }
            >
              {isSidebarCollapsed ? null : (
                <PanelLeftClose className="size-5 text-foreground-fg-quaternary-400 group-hover:text-quaternary-500" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <span>{isSidebarCollapsed ? 'Expand' : 'Collapse'}</span>
          </TooltipContent>
        </Tooltip>
      </div>

      {!isSidebarCollapsed && (
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="w-full truncate text-lg text-primary-900 min-h-7 font-semibold">
              {deliverableSet?.deliverable_set_name || ''}
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <span>{deliverableSet?.deliverable_set_name || ''}</span>
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  )
}
